import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signup(name, email, password);
      toast.success("Welcome to Badi.");
      nav(plan ? `/intake?plan=${plan}` : "/intake");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 py-20 max-w-md mx-auto">
        <p className="label-eyebrow mb-4">Begin the path</p>
        <h1 className="font-serif text-4xl text-[#2D322B] mb-10">Create your account</h1>
        <form onSubmit={onSubmit} className="space-y-6" data-testid="signup-form">
          <div>
            <label className="label-eyebrow block mb-2">Name</label>
            <input
              required value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="signup-name"
              className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Email</label>
            <input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="signup-email"
              className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-eyebrow">Password</label>
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="signup-toggle-password"
                className="text-[10px] uppercase tracking-widest text-[#5C6359] hover:text-[#C05C42] inline-flex items-center gap-1 transition-colors"
              >
                {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <input
              type={showPassword ? "text" : "password"} required value={password} minLength={6}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="signup-password"
              className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
            />
          </div>
          <button type="submit" disabled={busy} data-testid="signup-submit" className="btn-primary w-full">
            {busy ? "Opening the gate…" : "Create account"}
          </button>
        </form>
        <p className="mt-8 text-sm text-[#5C6359]">
          Already a member?{" "}
          <Link to="/login" className="text-[#C05C42] hover:underline" data-testid="signup-link-login">Sign in</Link>
        </p>
      </section>
      <Footer />
    </div>
  );
}
